import React from 'react';
import pt from 'prop-types';
import Container from '../../../modules/container';
import Headline, { headlinePropTypes } from '../../ui/headline';
import { combine } from '../../../helpers/styles';
import TextVisualColumn, {
  TextVisualColumnPropTypes,
} from './components/column';
import CustomScrollable from '../../ui/customScrollable';
import * as styles from './styles.module.scss';
import RichText, { richTextPropTypes } from '../../ui/richText';
import CMSAccordion from '../accordion';

const CMSTextVisual = ({
  headline,
  sublineAbove,
  beigeBackground,
  items,
  spacing,
  className,
  inContainer,
  mobileLayout,
  articleImageSizes,
  horizontalColsGap,
  accordionOpenIndex,
  accordionOpenId,
  accordionSearchIncluded,
}) => {
  const hasPodcast = items.some((item) => item.podcastId);
  const columnCount = items.length;
  const columnClass = ['one', 'two', 'three', 'four'];
  const imgAspectRatioClass = {
    'Large square': 'largeSquare',
    'Large landscape': 'largeLandscape',
    'Small square': 'smallSquare',
    'Small landscape': 'smallLandscape',
  };

  const ContentContainer = ({ children }) => {
    if (!mobileLayout || mobileLayout === 'Slider') {
      return (
        <CustomScrollable
          onlySmall
          className={combine(
            styles.columnWrapper,
            styles[columnClass[columnCount - 1]],
            ['Small square', 'Small landscape'].includes(articleImageSizes) &&
              styles.smallItems,
            inContainer && styles.inContainer,
            horizontalColsGap === 'Big' && styles.bigGap
          )}
        >
          {children}
        </CustomScrollable>
      );
    }

    return (
      <div
        className={combine(
          styles.columnWrapper,
          styles[columnClass[columnCount - 1]],
          styles[`sm${mobileLayout === '1 Column' ? 'one' : 'two'}`],
          ['Small square', 'Small landscape'].includes(articleImageSizes) &&
            styles.smallItems,
          inContainer && styles.inContainer,
          horizontalColsGap === 'Big' && styles.bigGap
        )}
      >
        {children}
      </div>
    );
  };

  return (
    <div
      className={combine(
        styles.wrapper,
        beigeBackground && styles.beigeBackground,
        spacing === 'Small' &&
          !inContainer &&
          !beigeBackground &&
          styles.smallSpacing,
        spacing === 'Big' &&
          !inContainer &&
          !beigeBackground &&
          styles.bigSpacing,
        className
      )}
    >
      <Container small={columnCount === 1}>
        {headline && (
          <Headline
            text={headline}
            type={inContainer ? 'h2' : 'p2'}
            variant={!inContainer && 'semibold'}
            level="h2"
            className={!inContainer && styles.hl}
          />
        )}
        {sublineAbove && (
          <RichText content={sublineAbove} className={styles.subline} />
        )}
        <ContentContainer>
          {items &&
            items.map((item, index) =>
              item.internal?.type === 'ContentfulContentAccordionWrapper' ? (
                <CMSAccordion
                  {...item}
                  defaultOpenIndex={
                    accordionOpenId === item.id ? accordionOpenIndex : null
                  }
                  inContainer
                  isSearchable={
                    false
                  } /* remove single search if container search enabled */
                />
              ) : (
                <TextVisualColumn
                  key={index}
                  {...item}
                  imgClass={imgAspectRatioClass[articleImageSizes]}
                  inContainer={inContainer}
                  className={combine(
                    mobileLayout && mobileLayout !== 'Slider' && styles.item
                  )}
                />
              )
            )}
        </ContentContainer>
      </Container>
    </div>
  );
};

CMSTextVisual.propTypes = {
  headline: pt.shape(headlinePropTypes),
  sublineAbove: pt.shape(richTextPropTypes),
  beigeBackground: pt.bool,
  items: pt.arrayOf(pt.shape(TextVisualColumnPropTypes)),
  spacing: pt.oneOf(['Small', 'Big']),
  articleImageSizes: pt.oneOf([
    'Large square',
    'Large landscape',
    'Small square',
    'Small landscape',
  ]),
  horizontalColsGap: pt.oneOf(['Default', 'Big']),
};

CMSTextVisual.defaultProps = {
  beigeBackground: false,
  items: [],
  spacing: 'Big',
  articleImageSizes: 'Large square',
  horizontalColsGap: 'Default',
};

export default CMSTextVisual;
